<template>
  <ACard id="system-access-log">
    <template #title>
      <span class="sal__title">
        <i class="material-icons-round sal__title-icon">history</i>
        시스템 접근 로그
      </span>
    </template>
    <template #action>
      <ABtn size="sm" icon="refresh" :loading="loading" @click="refreshLogs">새로고침</ABtn>
    </template>

    <div class="sal__filters">
      <div class="p-field">
        <label class="p-field__label">활동 유형</label>
        <select v-model="filters.actionType" class="p-input">
          <option value="">전체</option>
          <option v-for="type in filterOptions" :key="type.value" :value="type.value">{{ type.label }}</option>
        </select>
      </div>
      <div class="p-field">
        <label class="p-field__label">실행일</label>
        <input v-model="filters.executionDate" type="date" class="p-input" />
      </div>
    </div>

    <div v-if="loading" class="sal__loading">
      <div class="p-spinner"></div>
      <span>로그를 불러오는 중...</span>
    </div>

    <!--
      조회 실패를 "로그 없음"으로 뭉뚱그리면 서버 오류가 조용히 묻힙니다.
      실제로 이 화면은 백엔드 500 을 빈 상태로 표시해 문제를 오래 가렸습니다.
    -->
    <div v-else-if="loadError" class="sal__error">
      <AEmpty title="접근 로그를 불러오지 못했어요" :sub="loadError" size="sm" />
      <ABtn size="sm" icon="refresh" @click="refreshLogs">다시 시도</ABtn>
    </div>

    <AEmpty v-else-if="accessLogs.length === 0" title="접근 로그가 없어요" size="sm" />

    <ATable
      v-else
      :columns="columns"
      :rows="accessLogs"
      row-key="idx"
    >
      <template #cell:datetime="{ row }">
        <div>
          <div class="sal__date">{{ formatDate(row.createdAt) }}</div>
          <small class="sal__time">{{ formatTime(row.createdAt) }}</small>
        </div>
      </template>
      <template #cell:type="{ row }">
        <APill :kind="getActionTypePillKind(row.actionType)">
          <i class="material-icons-round sal__type-icon">{{ getActionTypeIcon(row.actionType) }}</i>
          {{ getActionTypeLabel(row.actionType) }}
        </APill>
      </template>
      <template #cell:action="{ row }">
        <div class="sal__action-cell">
          <div class="sal__action">{{ row.action }}</div>
          <small v-if="row.targetInfo" class="sal__target">{{ row.targetInfo }}</small>
          <!--
            metadata 는 활동 유형마다 담기는 키가 다릅니다.
            특정 스키마를 가정하지 않고 들어온 키를 그대로 펼쳐 보여줍니다.
          -->
          <button
            v-if="metadataEntries(row).length"
            type="button"
            class="sal__meta-toggle"
            @click="toggleMeta(row.idx)"
          >
            <i class="material-icons-round">{{ isMetaOpen(row.idx) ? 'expand_less' : 'expand_more' }}</i>
            상세 {{ metadataEntries(row).length }}항목
          </button>
          <dl v-if="isMetaOpen(row.idx)" class="sal__meta">
            <div v-for="entry in metadataEntries(row)" :key="entry.key" class="sal__meta-row">
              <dt>{{ entry.key }}</dt>
              <dd>{{ entry.value }}</dd>
            </div>
          </dl>
        </div>
      </template>
      <template #cell:ip="{ row }">
        <div class="sal__ip">
          <span class="sal__mono">{{ row.ipAddress }}</span>
          <small>{{ row.userAgent }}</small>
        </div>
      </template>
    </ATable>

    <APagination
      v-if="totalCount > 0"
      :current-page="currentPage"
      :total="totalCount"
      :page-size="pageSize"
      @change="goToPage"
    />
  </ACard>
</template>

<script>
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import { apiErrorMessage } from "@/utils/apiError";

export default {
  name: "SystemAccessLogCard",
  components: { ACard, ABtn, APill, ATable, AEmpty, APagination },
  props: {
    adminIdx: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      accessLogs: [],
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      filters: { actionType: "", executionDate: "" },
      openMetaIds: [],
      loadError: "",
      // 라벨·필터 사전. filterable: false 는 목록에 뜨면 라벨은 붙이되
      // 필터 드롭다운에서는 빼겠다는 뜻입니다.
      actionTypes: [
        // 로그인 시각은 관리 가치가 낮아 필터에서 제외합니다.
        // 과거 기록이 섞여 들어와도 라벨은 정상으로 보이도록 항목 자체는 남깁니다.
        { value: "login", label: "로그인", filterable: false },
        { value: "account_manage", label: "계정 관리" },
        { value: "content_manage", label: "컨텐츠 관리" },
        { value: "settlement", label: "정산 관리" },
        // 무료/사은품 삭제 등 결제 관리. 등록은 이력 연속성 때문에 settlement 로 남아 있습니다.
        { value: "payment_manage", label: "결제 관리" },
        { value: "system_config", label: "시스템 설정" },
        // 바우처 기관 · Master · 입금 · 잔액 관리(docs/바우처_기관발행_관리자화면_가이드.md §5).
        { value: "voucher_manage", label: "바우처 관리" },
      ],
      columns: [
        { key: "datetime", label: "일시", width: 160 },
        { key: "type", label: "유형", width: 130 },
        { key: "action", label: "활동 내용", wrap: true },
        { key: "ip", label: "IP / UA", width: 200 },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize) || 1;
    },
    filterOptions() {
      return this.actionTypes.filter((t) => t.filterable !== false);
    },
  },
  watch: {
    adminIdx: {
      handler(newVal) {
        if (newVal) {
          this.currentPage = 1;
          this.loadAccessLogs();
        }
      },
      immediate: true,
    },
    filters: {
      handler() {
        if (this.currentPage === 1) this.loadAccessLogs();
        else this.currentPage = 1;
      },
      deep: true,
    },
    currentPage(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) this.loadAccessLogs();
    },
  },
  methods: {
    async loadAccessLogs() {
      if (!this.adminIdx) return;
      this.loading = true;
      this.loadError = "";
      try {
        const params = { page: this.currentPage, pageSize: this.pageSize };
        if (this.filters.actionType) params.actionType = this.filters.actionType;
        if (this.filters.executionDate) params.executionDate = this.filters.executionDate;

        const response = await this.$axios({
          method: "get",
          url: `/admin/access-logs/${this.adminIdx}`,
          params,
          loadingBar: false,
        });

        if (response.status === 200 && response.data.success) {
          this.accessLogs = response.data.data.logs || [];
          this.totalCount = response.data.data.pagination?.totalCount || 0;
          this.openMetaIds = [];
        }
      } catch (error) {
        console.error("접근 로그 로드 실패:", error);
        this.accessLogs = [];
        this.totalCount = 0;
        this.openMetaIds = [];
        this.loadError = apiErrorMessage(error, "잠시 후 다시 시도해 주세요.");
      } finally {
        this.loading = false;
      }
    },
    refreshLogs() { this.loadAccessLogs(); },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) this.currentPage = page;
    },
    formatDate(s) {
      return new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
    },
    formatTime(s) {
      return new Date(s).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    },
    metadataEntries(row) {
      const meta = row.metadata;
      if (!meta || typeof meta !== "object" || Array.isArray(meta)) return [];
      return Object.entries(meta).map(([key, value]) => ({ key, value: this.formatMetaValue(value) }));
    },
    formatMetaValue(v) {
      if (v === null || v === undefined || v === "") return "—";
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    },
    toggleMeta(idx) {
      const i = this.openMetaIds.indexOf(idx);
      if (i === -1) this.openMetaIds.push(idx);
      else this.openMetaIds.splice(i, 1);
    },
    isMetaOpen(idx) {
      return this.openMetaIds.includes(idx);
    },
    getActionTypeLabel(type) {
      const t = this.actionTypes.find((x) => x.value === type);
      return t ? t.label : type;
    },
    getActionTypePillKind(type) {
      return { login: "ok", account_manage: "info", content_manage: "info", settlement: "warn", payment_manage: "warn", system_config: "dark", voucher_manage: "yellow" }[type] || "default";
    },
    getActionTypeIcon(type) {
      return { login: "login", account_manage: "manage_accounts", content_manage: "menu_book", settlement: "payments", payment_manage: "credit_card", system_config: "settings", voucher_manage: "confirmation_number" }[type] || "info";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.sal {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__filters {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 14px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px;
    color: $p-ink-mid;
    font-size: 12px;
  }

  &__date {
    font-weight: 700;
    color: $p-ink;
    font-size: 12px;
  }

  &__time {
    color: $p-ink-soft;
    font-family: $p-font-mono;
    font-size: 11px;
  }

  &__type-icon {
    font-size: 11px !important;
    margin-right: 2px;
  }

  // action / targetInfo / 상세 토글을 세로로 쌓습니다.
  // targetInfo 가 <small> 이라 그냥 두면 토글이 같은 줄에 붙습니다.
  &__action-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  &__action {
    font-weight: 700;
    color: $p-ink;
  }

  &__target {
    color: $p-ink-soft;
    line-height: 1.4;
  }

  // metadata 펼치기 — 하드 삭제처럼 되돌릴 수 없는 활동의 복구 근거를 화면에서 바로 봅니다.
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-bottom: 8px;
  }

  &__meta-toggle {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-top: 4px;
    padding: 2px 6px 2px 2px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-panel;
    color: $p-ink-mid;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: background $p-dur $p-ease, border-color $p-dur $p-ease;

    > i { font-size: 14px !important; }

    &:hover {
      background: $p-panel-alt;
      border-color: $p-line-hard;
    }
  }

  &__meta {
    margin: 6px 0 0;
    padding: 8px 10px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__meta-row {
    display: flex;
    gap: 8px;
    font-size: 11px;
    line-height: 1.5;

    > dt {
      flex-shrink: 0;
      min-width: 108px;
      color: $p-ink-soft;
      font-family: $p-font-mono;
      font-weight: 600;
    }

    > dd {
      margin: 0;
      color: $p-ink;
      word-break: break-all;
    }
  }

  &__ip {
    display: flex;
    flex-direction: column;
    gap: 2px;

    > small {
      color: $p-ink-soft;
      font-size: 10px;
    }
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink2;
  }
}
</style>
